// Mock API service for workspaces using localStorage

import {
  Workspace,
  WorkspaceVersion,
  SaveWorkspaceRequest,
  UpdateWorkspaceRequest,
  LoadWorkspaceOptions,
  LoadWorkspaceResponse,
  ListWorkspacesParams,
  ListWorkspacesResponse,
  WorkspaceListItem,
  StoredWorkspaceData,
} from '@/types/workspace';
import { FailureRecord } from '@/types/failure';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { createSnapshot, validateSnapshot } from '@/utils/workspaceSnapshots';
import { createVersion, generateAutoChangesSummary } from '@/utils/workspaceVersioning';

// Storage keys
const STORAGE_KEYS = {
  WORKSPACES: 'weibull_workspaces',
  WORKSPACE_DATA: (id: string) => `weibull_workspace_data_${id}`,
  USER_PREFERENCES: 'weibull_workspace_preferences',
} as const;

/**
 * Get current user ID from parameter or fallback
 */
const getCurrentUserId = (userId?: string): string => {
  if (userId) {
    return userId;
  }
  
  // Check auth configuration
  const useMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';
  const useMockAPI = process.env.REACT_APP_USE_MOCK_API === 'true';
  
  // In mock auth/API mode, use a consistent mock ID
  if (useMockAuth || useMockAPI) {
    return 'mock_user_123';
  }
  
  // For production with real auth, userId parameter is required
  throw new Error('User ID is required for workspace operations');
};

/**
 * Get mock user information by user ID
 * Only used when both mock auth and mock API are enabled
 */
const getMockUserInfo = (userId: string): { id: string; name: string; email?: string } => {
  // Check if we should use mock users at all
  const useMockAuth = process.env.REACT_APP_USE_MOCK_AUTH === 'true';
  const useMockAPI = process.env.REACT_APP_USE_MOCK_API === 'true';
  
  // Only return mock users when both are enabled
  if (useMockAuth && useMockAPI) {
    // Simplified mock user database for development only
    const mockUsers: Record<string, { id: string; name: string; email: string }> = {
      'mock_user_123': {
        id: 'mock_user_123',
        name: 'Development User',
        email: 'dev.user@company.com'
      }
    };
    
    return mockUsers[userId] || {
      id: userId,
      name: 'Development User',
      email: 'dev.user@company.com'
    };
  }
  
  // For real users, return minimal info (should be enriched by frontend)
  // This handles the case where mock API is used but real auth provides the user
  return {
    id: userId,
    name: userId.split('@')[0] || 'User', // Extract name from email if possible
    email: userId.includes('@') ? userId : 'user@onyxinsight.com'
  };
};

/**
 * Mock API service for workspaces using localStorage
 */
export const mockWorkspacesApi = {
  /**
   * List user's workspaces with filtering and sorting
   */
  async list(params: ListWorkspacesParams = {}, userId?: string): Promise<ListWorkspacesResponse> {
    const {
      page = 1,
      size = 20,
      search = '',
      tags = [],
      sortBy = 'updatedAt',
      sortOrder = 'desc',
    } = params;

    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    let workspaces = data.workspaces.filter((w) => w.metadata.userId === currentUserId);

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      workspaces = workspaces.filter(
        (w) =>
          w.name.toLowerCase().includes(searchLower) ||
          w.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply tag filter
    if (tags.length > 0) {
      workspaces = workspaces.filter((w) => w.metadata.tags?.some((tag) => tags.includes(tag)));
    }

    // Sort workspaces
    workspaces.sort((a, b) => {
      const aVal = a.metadata[sortBy as keyof typeof a.metadata] || a[sortBy as keyof typeof a];
      const bVal = b.metadata[sortBy as keyof typeof b.metadata] || b[sortBy as keyof typeof b];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comparison = aVal.localeCompare(bVal);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });

    // Apply pagination
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const paginatedWorkspaces = workspaces.slice(startIndex, endIndex);

    // Convert to list items
    const workspaceItems: WorkspaceListItem[] = paginatedWorkspaces.map((w) => {
      const workspaceDate = new Date(w.metadata.updatedAt);
      const mockLastDataUpdate = new Date('2025-08-01T00:00:00.000Z'); // Mock recent data update
      const isLatestData = workspaceDate >= mockLastDataUpdate;

      return {
        id: w.id,
        name: w.name,
        description: w.description,
        version: w.metadata.version,
        createdAt: w.metadata.createdAt,
        updatedAt: w.metadata.updatedAt,
        tags: w.metadata.tags,
        isLatestData,
        hasResults: !!w.results, // Add hasResults property based on whether workspace has analysis results
        createdBy: w.createdBy,
        metadata: {
          totalFailures: w.dataSnapshot?.totalRecords || w.metadata.dataContext.totalAvailableRecords,
          dateRange: w.metadata.dataContext.dateRange,
          sitesIncluded: w.metadata.dataContext.sitesIncluded,
          componentsAnalyzed: w.metadata.dataContext.componentsAnalyzed,
        },
      };
    });

    return {
      workspaces: workspaceItems,
      total: workspaces.length,
      page,
      size,
    };
  },

  /**
   * Save a new workspace
   */
  async save(
    request: SaveWorkspaceRequest,
    currentFailures?: FailureRecord[],
    currentResults?: WeibullAnalysisResult,
    userId?: string
  ): Promise<Workspace> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);

    // Check for duplicate names
    const existingNames = data.workspaces
      .filter((w) => w.metadata.userId === currentUserId)
      .map((w) => w.name);

    if (existingNames.includes(request.workspace_name)) {
      throw new Error(`Workspace named "${request.workspace_name}" already exists`);
    }

    const now = new Date().toISOString();
    const workspaceId = generateId();

    // Create data context from current filters and analysis results
    // Try to get the most accurate count possible
    let totalRecordsCount = 0;

    if (currentResults?.summary?.sampleSize) {
      // Best option: Use the actual analysis sample size
      totalRecordsCount = currentResults.summary.sampleSize;
    } else if (currentFailures?.length) {
      // Fallback: Use current failures count (may be paginated)
      totalRecordsCount = currentFailures.length;
    }

    const dataContext = {
      totalAvailableRecords: totalRecordsCount,
      dateRange: {
        from: request.configuration.filters.dateFrom || '',
        to: request.configuration.filters.dateTo || '',
      },
      sitesIncluded: request.configuration.filters.sites || [],
      componentsAnalyzed: [
        ...(request.configuration.filters.componentsMajor || []),
        ...(request.configuration.filters.componentsMinor || []),
      ],
    };

    // Get user information for display
    const userInfo = getMockUserInfo(currentUserId);
    
    const workspace: Workspace = {
      id: workspaceId,
      name: request.workspace_name,
      description: request.description,
      configuration: request.configuration,
      dataSnapshot: currentFailures ? createSnapshot(currentFailures) : undefined,
      results: currentResults || undefined,
      metadata: {
        userId: currentUserId,
        createdAt: now,
        updatedAt: now,
        version: 1,
        tags: request.tags,
        dataContext,
      },
      createdBy: {
        id: userInfo.id,
        name: userInfo.name,
        email: userInfo.email,
      },
    };

    // Store workspace
    data.workspaces.push(workspace);
    saveStoredData(data);

    return workspace;
  },

  /**
   * Load a workspace by ID
   */
  async load(id: string, options: LoadWorkspaceOptions = {}, userId?: string): Promise<LoadWorkspaceResponse> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    const workspace = data.workspaces.find(
      (w) => w.id === id && w.metadata.userId === currentUserId
    );

    if (!workspace) {
      throw new Error(`Workspace not found: ${id}`);
    }

    const response: LoadWorkspaceResponse = {
      workspace,
      warnings: [],
    };

    // Handle snapshot validation if using snapshot data
    if (options.preserveData && workspace.dataSnapshot) {
      const validation = validateSnapshot(workspace.dataSnapshot);
      if (!validation.isValid) {
        response.warnings = validation.warnings;
      }
    }

    // Simulate data freshness check for dynamic loading
    if (options.preserveData !== true) {
      // In real implementation, this would check current database state
      response.currentData = {
        availableRecords: workspace.metadata.dataContext.totalAvailableRecords,
        isDataChanged: false, // Mock: assume no changes
        dataTimestamp: new Date().toISOString(),
      };
    }

    return response;
  },

  /**
   * Update an existing workspace
   */
  async update(
    id: string,
    request: UpdateWorkspaceRequest,
    currentFailures?: FailureRecord[],
    _currentResults?: WeibullAnalysisResult,
    userId?: string,
    userName?: string
  ): Promise<Workspace> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    const workspaceIndex = data.workspaces.findIndex(
      (w) => w.id === id && w.metadata.userId === currentUserId
    );

    if (workspaceIndex === -1) {
      throw new Error(`Workspace not found: ${id}`);
    }

    const existingWorkspace = data.workspaces[workspaceIndex];

    // Check for name conflicts (if name is being changed)
    if (request.workspace_name && request.workspace_name !== existingWorkspace.name) {
      const existingNames = data.workspaces
        .filter((w) => w.metadata.userId === currentUserId && w.id !== id)
        .map((w) => w.name);

      if (existingNames.includes(request.workspace_name)) {
        throw new Error(`Workspace named "${request.workspace_name}" already exists`);
      }
    }

    // Create version history entry
    const versions = data.versions[id] || [];
    const newVersion = createVersion(
      existingWorkspace,
      generateAutoChangesSummary(existingWorkspace, {
        ...existingWorkspace,
        name: request.workspace_name || existingWorkspace.name,
        description: request.description ?? existingWorkspace.description,
        configuration: {
        ...existingWorkspace.configuration,
        ...request.configuration,
        },
        } as Workspace),
        currentUserId,
        userName // Pass username for display
        );

    versions.push(newVersion);
    data.versions[id] = versions;

    const now = new Date().toISOString();

    // Update workspace
    const updatedWorkspace: Workspace = {
      ...existingWorkspace,
      name: request.workspace_name || existingWorkspace.name,
      description: request.description ?? existingWorkspace.description,
      configuration: {
        ...existingWorkspace.configuration,
        ...request.configuration,
      },
      metadata: {
        ...existingWorkspace.metadata,
        updatedAt: now,
        version: existingWorkspace.metadata.version + 1,
        tags: request.tags ?? existingWorkspace.metadata.tags,
      },
    };

    // Update data context if filters changed
    if (request.configuration?.filters) {
      updatedWorkspace.metadata.dataContext = {
        totalAvailableRecords:
          currentFailures?.length || updatedWorkspace.metadata.dataContext.totalAvailableRecords,
        dateRange: {
          from:
            request.configuration.filters.dateFrom ||
            updatedWorkspace.metadata.dataContext.dateRange.from,
          to:
            request.configuration.filters.dateTo ||
            updatedWorkspace.metadata.dataContext.dateRange.to,
        },
        sitesIncluded:
          request.configuration.filters.sites ||
          updatedWorkspace.metadata.dataContext.sitesIncluded,
        componentsAnalyzed: [
          ...(request.configuration.filters.componentsMajor ||
            updatedWorkspace.configuration.filters.componentsMajor ||
            []),
          ...(request.configuration.filters.componentsMinor ||
            updatedWorkspace.configuration.filters.componentsMinor ||
            []),
        ],
      };
    }

    data.workspaces[workspaceIndex] = updatedWorkspace;
    saveStoredData(data);

    return updatedWorkspace;
  },

  /**
   * Delete a workspace
   */
  async delete(id: string, userId?: string): Promise<void> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    const workspaceIndex = data.workspaces.findIndex(
      (w) => w.id === id && w.metadata.userId === currentUserId
    );

    if (workspaceIndex === -1) {
      throw new Error(`Workspace not found: ${id}`);
    }

    // Remove workspace and its versions
    data.workspaces.splice(workspaceIndex, 1);
    delete data.versions[id];

    saveStoredData(data);
  },

  /**
   * Get version history for a workspace
   */
  async getVersions(id: string, userId?: string): Promise<WorkspaceVersion[]> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    const workspace = data.workspaces.find(
      (w) => w.id === id && w.metadata.userId === currentUserId
    );

    if (!workspace) {
      throw new Error(`Workspace not found: ${id}`);
    }

    return data.versions[id] || [];
  },

  /**
   * Restore a specific version of a workspace
   */
  async restoreVersion(id: string, versionNumber: number, userId?: string, userName?: string): Promise<Workspace> {
    const data = getStoredData();
    const currentUserId = getCurrentUserId(userId);
    const versions = data.versions[id] || [];
    const version = versions.find((v) => v.versionNumber === versionNumber);

    if (!version) {
      throw new Error(`Version ${versionNumber} not found for workspace ${id}`);
    }

    // Create new version from current state before restoring
    const currentWorkspaceIndex = data.workspaces.findIndex(
      (w) => w.id === id && w.metadata.userId === currentUserId
    );
    if (currentWorkspaceIndex !== -1) {
      const currentWorkspace = data.workspaces[currentWorkspaceIndex];
      const backupVersion = createVersion(
        currentWorkspace,
        `Backup before restoring to v${versionNumber}`,
        currentUserId,
        userName // Pass username for display
      );
      versions.push(backupVersion);
    }

    // Restore the workspace (update version number and timestamp)
    const restoredWorkspace: Workspace = {
      ...version.workspace,
      metadata: {
        ...version.workspace.metadata,
        updatedAt: new Date().toISOString(),
        version: (version.workspace.metadata.version || 0) + 1,
      },
    };

    if (currentWorkspaceIndex !== -1) {
      data.workspaces[currentWorkspaceIndex] = restoredWorkspace;
    }

    data.versions[id] = versions;
    saveStoredData(data);

    return restoredWorkspace;
  },
};

/**
 * Get stored workspace data from localStorage
 */
function getStoredData(): StoredWorkspaceData {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WORKSPACES);
    if (stored) {
      const data = JSON.parse(stored) as StoredWorkspaceData;
      return data;
    }
  } catch (error) {
    console.warn('Failed to load workspace data from localStorage:', error);
  }

  return initializeStorage();
}

/**
 * Save workspace data to localStorage
 */
function saveStoredData(data: StoredWorkspaceData): void {
  try {
    localStorage.setItem(STORAGE_KEYS.WORKSPACES, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save workspace data to localStorage:', error);
    throw new Error('Failed to save workspace. Storage may be full.');
  }
}

/**
 * Initialize empty storage structure
 */
function initializeStorage(): StoredWorkspaceData {
  const data: StoredWorkspaceData = {
    workspaces: [],
    versions: {},
    lastCleanup: new Date().toISOString(),
  };

  saveStoredData(data);
  return data;
}

/**
 * Generate a unique ID for workspaces
 */
function generateId(): string {
  return `workspace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Cleanup old data (can be called periodically)
 */
export function cleanupWorkspaceStorage(maxAge: number = 90 * 24 * 60 * 60 * 1000): void {
  const data = getStoredData();
  const now = Date.now();

  // Remove old workspaces
  const cutoffDate = now - maxAge;
  data.workspaces = data.workspaces.filter((w) => {
    const lastUpdated = new Date(w.metadata.updatedAt).getTime();
    return lastUpdated > cutoffDate;
  });

  // Remove versions for deleted workspaces
  const existingWorkspaceIds = new Set(data.workspaces.map((w) => w.id));
  for (const workspaceId in data.versions) {
    if (!existingWorkspaceIds.has(workspaceId)) {
      delete data.versions[workspaceId];
    }
  }

  data.lastCleanup = new Date().toISOString();
  saveStoredData(data);
}

/**
 * Utility function to get current mock workspaces (for testing)
 */
export function getMockWorkspaces(userId?: string): Workspace[] {
  const data = getStoredData();
  const currentUserId = getCurrentUserId(userId);
  return data.workspaces.filter((w) => w.metadata.userId === currentUserId);
}

/**
 * Utility function to set mock workspaces (for testing)
 */
export function setMockWorkspaces(workspaces: Workspace[], userId?: string): void {
  const data = getStoredData();
  const currentUserId = getCurrentUserId(userId);
  // Remove existing workspaces for current user
  data.workspaces = data.workspaces.filter((w) => w.metadata.userId !== currentUserId);
  // Add new workspaces with current user ID
  data.workspaces.push(
    ...workspaces.map((w) => ({
      ...w,
      metadata: { ...w.metadata, userId: currentUserId },
    }))
  );
  saveStoredData(data);
}

/**
 * Utility function to clear all mock workspaces (for testing)
 */
export function clearMockWorkspaces(userId?: string): void {
  const data = getStoredData();
  const currentUserId = getCurrentUserId(userId);
  data.workspaces = data.workspaces.filter((w) => w.metadata.userId !== currentUserId);
  // Also clear versions for current user workspaces
  for (const workspaceId in data.versions) {
    const workspace = data.workspaces.find((w) => w.id === workspaceId);
    if (!workspace) {
      delete data.versions[workspaceId];
    }
  }
  saveStoredData(data);
}

/**
 * Utility function to generate a mock workspace (for testing)
 */
export function generateMockWorkspace(overrides: Partial<Workspace> = {}, userId?: string): Workspace {
  const now = new Date().toISOString();
  const id = generateId();
  const currentUserId = getCurrentUserId(userId);
  const userInfo = getMockUserInfo(currentUserId);

  return {
    id,
    name: `Mock Workspace ${Math.random().toString(36).substr(2, 5)}`,
    description: 'A mock workspace for testing',
    configuration: {
      filters: {
        page: 1,
        size: 20,
        sites: ['Test Site'],
        turbineModels: ['Test Model'],
        componentsMajor: ['Gearbox'],
        componentsMinor: [],
        failureModes: [],
        outcomeName: [],
        excludedCaseIds: [],
        dateFrom: '2020-01-01',
        dateTo: '2023-12-31',
      },
      analysisParams: {
        showConfidenceIntervals: true,
        confidenceLevel: 95,
        showCensoredData: true,
      },
    },
    metadata: {
      userId: currentUserId,
      createdAt: now,
      updatedAt: now,
      version: 1,
      tags: ['test'],
      dataContext: {
        totalAvailableRecords: 100,
        dateRange: { from: '2020-01-01', to: '2023-12-31' },
        sitesIncluded: ['Test Site'],
        componentsAnalyzed: ['Gearbox'],
      },
    },
    createdBy: {
      id: userInfo.id,
      name: userInfo.name,
      email: userInfo.email,
    },
    ...overrides,
  };
}

/**
 * Generate sample workspaces with user information for demonstration
 * This should be called once to populate initial data for development
 */
export function generateSampleWorkspaces(): void {
  const data = getStoredData();
  
  // Only generate if no workspaces exist to avoid duplicates
  if (data.workspaces.length > 0) {
    return;
  }

  // Get current user to create sample workspaces
  const currentUserId = getCurrentUserId();
  const currentUserInfo = getMockUserInfo(currentUserId);

  const sampleWorkspaces: Workspace[] = [
    generateMockWorkspace({
      name: 'Gearbox Reliability Analysis Q3 2024',
      description: 'Comprehensive analysis of gearbox failures across all sites for Q3 2024 reporting',
      configuration: {
        filters: {
          page: 1,
          size: 50,
          sites: ['Hill of Towie', 'Whitelee', 'Beatrice'],
          turbineModels: ['V90-2.0', 'SG-2.1'],
          componentsMajor: ['Gearbox'],
          componentsMinor: ['High Speed Bearing', 'Input Shaft'],
          failureModes: ['Bearing Failure', 'Oil Leak'],
          outcomeName: ['Repair', 'Replace'],
          excludedCaseIds: [],
          dateFrom: '2024-07-01',
          dateTo: '2024-09-30',
        },
        analysisParams: {
          showConfidenceIntervals: true,
          confidenceLevel: 95,
          showCensoredData: true,
        },
      },
      results: {
        parameters: {
          shape: 2.3,  // Beta parameter
          scale: 18.5, // Eta parameter in years
          rSquared: 0.94
        },
        plotData: {
          operationTimes: [],
          probabilities: [],
          fittedLine: []
        },
        summary: {
          sampleSize: 247,
          meanLife: 16.8,
          medianLife: 15.9,
          characteristicLife: 18.5
        }
      },
      metadata: {
        userId: currentUserId,
        createdAt: '2024-10-15T09:30:00.000Z',
        updatedAt: '2024-10-15T14:22:00.000Z',
        version: 2,
        tags: ['quarterly-report', 'gearbox', 'production'],
        dataContext: {
          totalAvailableRecords: 247,
          dateRange: { from: '2024-07-01', to: '2024-09-30' },
          sitesIncluded: ['Hill of Towie', 'Whitelee', 'Beatrice'],
          componentsAnalyzed: ['Gearbox'],
        },
      },
    }, currentUserId),
    
    generateMockWorkspace({
      name: 'Generator Bearing Study',
      description: 'Deep dive analysis into generator bearing performance trends',
      configuration: {
        filters: {
          page: 1,
          size: 100,
          sites: ['Beatrice'],
          turbineModels: ['SG-8.0'],
          componentsMajor: ['Generator'],
          componentsMinor: ['Main Bearing'],
          failureModes: ['Vibration', 'Temperature'],
          outcomeName: ['Monitor', 'Repair'],
          excludedCaseIds: [],
          dateFrom: '2023-01-01',
          dateTo: '2024-12-31',
        },
        analysisParams: {
          showConfidenceIntervals: true,
          confidenceLevel: 90,
          showCensoredData: false,
        },
      },
      results: {
        parameters: {
          shape: 1.8,  // Beta parameter
          scale: 12.2, // Eta parameter in years  
          rSquared: 0.89
        },
        plotData: {
          operationTimes: [],
          probabilities: [],
          fittedLine: []
        },
        summary: {
          sampleSize: 89,
          meanLife: 11.1,
          medianLife: 10.2,
          characteristicLife: 12.2
        }
      },
      metadata: {
        userId: currentUserId,
        createdAt: '2024-08-12T11:15:00.000Z',
        updatedAt: '2024-08-12T11:15:00.000Z',
        version: 1,
        tags: ['generator', 'bearing', 'research'],
        dataContext: {
          totalAvailableRecords: 89,
          dateRange: { from: '2023-01-01', to: '2024-12-31' },
          sitesIncluded: ['Beatrice'],
          componentsAnalyzed: ['Generator'],
        },
      },
    }, currentUserId),
  ];

  // Set creator information for all sample workspaces
  sampleWorkspaces.forEach((workspace) => {
    workspace.createdBy = {
      id: currentUserInfo.id,
      name: currentUserInfo.name,
      email: currentUserInfo.email,
    };
  });

  data.workspaces.push(...sampleWorkspaces);
  saveStoredData(data);
  
}
